// frontend/src/pages/ManageStudents.js
// API used:
//   GET /api/students?search=...&roster_id=...&house_id=...
//   POST /api/students            { first_name, last_name, email?, roster_id?, house_id? }
//   PATCH /api/students/:id       { ...fields }
//   DELETE /api/students/:id
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";

/* Tiny breakpoint hook */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function ManageStudents() {
  const isMobile = useIsMobile();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });

  const q = useMemo(() => ({ search: search || undefined }), [search]);

  async function load() {
    setBusy(true);
    setErr("");
    const res = await api.get("/api/students", { query: q });
    if (!res.ok) {
      setErr(res?.data?.detail || "Unable to load students.");
      setRows([]);
    } else {
      const list = Array.isArray(res.data?.results)
        ? res.data.results
        : Array.isArray(res.data)
        ? res.data
        : [];
      setRows(list);
    }
    setBusy(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function createStudent(e) {
    e.preventDefault();
    const res = await api.post("/api/students", {
      first_name: form.first_name?.trim(),
      last_name: form.last_name?.trim(),
      email: form.email?.trim() || undefined,
    });
    if (!res.ok) return alert(res?.data?.detail || "Create failed.");
    setForm({ first_name: "", last_name: "", email: "" });
    setCreating(false);
    load();
  }

  async function removeStudent(id) {
    if (!window.confirm("Delete this student?")) return;
    const res = await api.del(`/api/students/${id}`);
    if (!res.ok) return alert(res?.data?.detail || "Delete failed.");
    load();
  }

  const sx = computeStyles(isMobile);

  return (
    <div style={sx.page}>
      <h1 style={sx.h1}>Manage Students</h1>

      <div style={sx.toolbar}>
        <input
          style={sx.input}
          placeholder="Search students…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button style={sx.btnPrimary} onClick={() => setCreating(true)}>
          + New Student
        </button>
      </div>

      {busy && <div>Loading…</div>}
      {err && <div style={sx.error}>{err}</div>}

      {!busy && !err && (
        <div style={sx.tableCard}>
          <div style={sx.tableScroll}>
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>First</th>
                  <th style={sx.th}>Last</th>
                  <th style={sx.th}>Email</th>
                  <th style={sx.th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: 12 }}>
                      No students yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={sx.td}>{r.first_name}</td>
                    <td style={sx.td}>{r.last_name}</td>
                    <td style={sx.td}>{r.email}</td>
                    <td style={sx.tdRight}>
                      <button
                        style={sx.btnDanger}
                        onClick={() => removeStudent(r.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <div style={sx.modal}>
          <form onSubmit={createStudent} style={sx.card}>
            <h3 style={sx.h3}>New Student</h3>
            <input
              style={sx.input}
              placeholder="First name"
              required
              value={form.first_name}
              onChange={(e) =>
                setForm({ ...form, first_name: e.target.value })
              }
            />
            <input
              style={sx.input}
              placeholder="Last name"
              required
              value={form.last_name}
              onChange={(e) =>
                setForm({ ...form, last_name: e.target.value })
              }
            />
            <input
              style={sx.input}
              placeholder="Email (optional)"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <div style={sx.row}>
              <button style={sx.btnPrimary} type="submit">
                Create
              </button>
              <button
                style={sx.btnGhost}
                type="button"
                onClick={() => setCreating(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function computeStyles(isMobile) {
  return {
    page: { display: "grid", gap: 16 },
    h1: { margin: 0, fontSize: 20, color: "#003e73", fontWeight: 800 },
    h3: { margin: "4px 0 8px", fontSize: 16, color: "#003e73" },

    /* Toolbar wraps on mobile; button can grow full-width */
    toolbar: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: isMobile ? "wrap" : "nowrap",
    },

    /* Inputs: mobile-safe font-size to prevent iOS zoom */
    input: {
      height: 36,
      borderRadius: 8,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "0 10px",
      width: isMobile ? "100%" : 280,
      boxSizing: "border-box",
      fontSize: isMobile ? 16 : 14,
      flex: isMobile ? "1 1 100%" : "0 0 auto",
    },

    btnPrimary: {
      height: 36,
      border: "none",
      borderRadius: 8,
      padding: "0 14px",
      background: "#f9a21a",
      color: "#fff",
      fontWeight: 800,
      cursor: "pointer",
      flex: isMobile ? "1 1 100%" : "0 0 auto",
      width: isMobile ? "100%" : "auto",
      fontSize: isMobile ? 16 : 14,
    },
    btnGhost: {
      height: 36,
      border: "1px solid rgba(0,0,0,0.2)",
      borderRadius: 8,
      padding: "0 14px",
      background: "#fff",
      color: "#333",
      cursor: "pointer",
      fontSize: isMobile ? 16 : 14,
    },
    btnDanger: {
      height: 30,
      border: "none",
      borderRadius: 8,
      padding: "0 12px",
      background: "#c62828",
      color: "#fff",
      cursor: "pointer",
    },

    error: {
      color: "#b00020",
      background: "#FFEAEA",
      border: "1px solid #F5B5B5",
      borderRadius: 8,
      padding: "8px 10px",
    },

    /* Card container for the table so borders/rounding stay intact */
    tableCard: {
      background: "#fff",
      borderRadius: 12,
      border: "1px solid rgba(0,0,0,0.08)",
      overflow: "hidden",
    },

    /* Horizontal scroll on small screens */
    tableScroll: {
      width: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
    },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: isMobile ? 520 : "auto", // avoid unreadable columns
    },
    th: {
      textAlign: "left",
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background: "#f7f9fc",
      whiteSpace: "nowrap",
    },
    td: {
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      whiteSpace: "nowrap",
    },
    tdRight: {
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      textAlign: "right",
      whiteSpace: "nowrap",
    },

    /* Modal & form */
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.25)",
      display: "grid",
      placeItems: "center",
      padding: 12,
      zIndex: 100,
    },
    card: {
      width: isMobile ? "100%" : 420,
      maxWidth: 480,
      background: "#fff",
      borderRadius: 12,
      padding: 16,
      display: "grid",
      gap: 8,
      border: "1px solid rgba(0,0,0,0.08)",
      boxSizing: "border-box",
    },

    row: {
      display: "flex",
      gap: 8,
      justifyContent: isMobile ? "stretch" : "flex-end",
      flexWrap: isMobile ? "wrap" : "nowrap",
    },
  };
}
